// One tagger: its vocabulary, its runs, and the three things an operator does
// here — change a tag, try it, and run it over some subjects.
//
// The page is written against `TaggerInfo`, never against a particular tagger:
// the answer shapes and preconditions offered in the add-a-tag form are the ones
// THIS tagger declared, so an operator cannot create a tag the tagger would then
// refuse to ask.
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Badge, Button, useToast } from "@bi/shared";
import type { SchemaDiff, SubjectFilters, TagDef, TagDraft, TagSetDiff } from "@bi/shared/types";

type Tri = SubjectFilters["hasHandoff"];

import { EmptyState, ErrorState, LoadingState, errorMessage } from "../components/states";
import {
  createDraft,
  createTag,
  deleteDraft,
  downloadDraft,
  exportTagset,
  fetchDraftDiff,
  fetchRunCounts,
  fetchRunFindings,
  fetchRunInputs,
  importTagset,
  startTagRun,
  updateDraft,
  updateTag,
} from "../lib/api";
import {
  corpusQuery,
  draftsQuery,
  onlineSchemaQuery,
  runCompareQuery,
  taggerSchemaQuery,
  taggersQuery,
  taggingIdentityQuery,
  taggingKeys,
  tagRunsQuery,
  tagsQuery,
} from "../lib/queries";
import { runDebugBundle, runFileStem, runReportHtml } from "../lib/run-export";
import {
  blockDiffLines,
  changeValue,
  diffSummary,
  draftStateTone,
  schemaSourceLabel,
  shortSha,
} from "../lib/schema-diff";

function downloadJson(filename: string, data: unknown): void {
  downloadText(filename, JSON.stringify(data, null, 2));
}

/** Save `text` as a file, byte for byte. A draft is downloaded as the bytes the
 *  backend serves so the file an engineer commits hashes the same on both sides.
 *  The MIME type is passed because a run report is HTML: saved as JSON it opens
 *  in an editor rather than in the browser, which is the whole point of it. */
function downloadText(filename: string, text: string, mime = "application/json"): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the NEXT task, never synchronously: a synchronous revoke can race
  // the browser's own read of the blob and cancel the save.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** queued/running/done/error, coloured so a failed run is findable in a long list. */
function RunStatus({ status }: { status: string }) {
  const tone =
    status === "done"
      ? "success"
      : status === "error"
        ? "danger"
        : status === "running"
          ? "info"
          : "neutral";
  return (
    <Badge size="sm" tone={tone} variant="soft">
      {status}
    </Badge>
  );
}

function when(iso: string | null | undefined): string {
  return (iso ?? "").slice(0, 16).replace("T", " ");
}

/** draft / promoted / diverged, coloured. */
function DraftState({ state }: { state: string }) {
  return (
    <Badge size="sm" tone={draftStateTone(state)} variant="soft">
      {state}
    </Badge>
  );
}

function answerShape(t: TagDef): string {
  if (t.answerType === "single_select") return `one of: ${t.options.join(", ")}`;
  if (t.answerType === "free_text") return "a sentence";
  return "yes / no";
}

export function TaggerPage({ name }: { name: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const taggers = useQuery(taggersQuery);
  const tags = useQuery(tagsQuery(name));
  const runs = useQuery(tagRunsQuery(name));
  const me = useQuery(taggingIdentityQuery);
  const corpora = useQuery(corpusQuery);
  const canWrite = me.data?.canWrite === true;

  const info = useMemo(() => taggers.data?.find((t) => t.name === name), [taggers.data, name]);

  const [draft, setDraft] = useState({
    name: "",
    definition: "",
    answerType: "yes_no",
    options: "",
    precondition: "none",
    scopeRef: "",
  });
  // The tag row open for editing, and its unsaved copy. Null when the table is
  // read-only, which is the state it is in almost always.
  const [editTag, setEditTag] = useState<{ id: string; name: string; definition: string } | null>(
    null,
  );
  // Which run is currently being fetched for its HTML report. A run's findings
  // are not in the runs list (that carries counts only), so the file is built on
  // demand.
  const [downloading, setDownloading] = useState("");
  // Separate from `downloading`: the debug file is the bigger, slower one (it
  // pages the captured inputs), and a reader who clicked the wrong button needs
  // to see WHICH one is working.
  const [debugging, setDebugging] = useState("");
  const [scopeKind, setScopeKind] = useState<"recent" | "explicit" | "rerun" | "corpus">("recent");
  const [batchId, setBatchId] = useState("");
  const selectedCorpus = (corpora.data ?? []).find((b) => b.id === batchId);
  const [sinceDays, setSinceDays] = useState(1);
  const [limit, setLimit] = useState(25);
  // The objective narrowing of a `recent` cover. Every field is a product fact
  // (a status, a flag, a count, a clock), never a judgement; the defaults mean
  // "don't care", which is the cover as it was before these existed.
  const [filters, setFilters] = useState<SubjectFilters>({
    briefStatus: "any",
    excludeSellers: false,
    excludeGuests: false,
    idleHours: null,
    minMessages: null,
    maxMessages: null,
    hasSearchRun: "any",
    hasConciergeRun: "any",
    hasOffer: "any",
    hasHandoff: "any",
    hasSearchAsYouGo: "any",
    clientIntent: "any",
    engagementType: "any",
    companySizes: [],
    businessTypes: [],
    businessStages: [],
    hasCompany: "any",
    hasWebsite: "any",
    hasIndustry: "any",
    hasBuyerIdentity: "any",
  });
  const setFilter = <K extends keyof SubjectFilters>(key: K, value: SubjectFilters[K]) =>
    setFilters((f) => ({ ...f, [key]: value }));
  const numberOrNull = (raw: string) => (raw.trim() === "" ? null : Number(raw));
  const [projectIds, setProjectIds] = useState("");
  const [rerunOf, setRerunOf] = useState("");
  const [onlyTags, setOnlyTags] = useState<string[]>([]);
  const [importText, setImportText] = useState("");
  const [diff, setDiff] = useState<TagSetDiff | null>(null);
  const [schemaText, setSchemaText] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const schema = useQuery(taggerSchemaQuery(name, Boolean(info?.schemaName)));

  // ---- the playground: what is online, drafts of it, two runs compared ----
  const hasSchema = Boolean(info?.schemaName);
  const online = useQuery(onlineSchemaQuery(name, hasSchema));
  const drafts = useQuery(draftsQuery(name, hasSchema));
  // Which schema a run asks: "online" (the ingested one, or the vendored file
  // when nothing is ingested yet), a draft id, or "paste" (the textarea).
  const [schemaChoice, setSchemaChoice] = useState("online");
  // The draft open in the editor, and its unsaved text.
  const [editing, setEditing] = useState<{ id: string; name: string; text: string } | null>(null);
  const [draftDiff, setDraftDiff] = useState<SchemaDiff | null>(null);
  const [newDraftName, setNewDraftName] = useState("");
  const [showOnline, setShowOnline] = useState(false);
  const [cmpA, setCmpA] = useState("");
  const [cmpB, setCmpB] = useState("");
  const [cmpStatus, setCmpStatus] = useState("");
  const compare = useQuery(runCompareQuery(cmpA, cmpB, cmpStatus));

  const invalidateTags = () => void qc.invalidateQueries({ queryKey: taggingKeys.tags(name) });
  const invalidateDrafts = () =>
    void qc.invalidateQueries({ queryKey: taggingKeys.drafts(name) });
  const fail = (e: unknown) => toast.add({ title: errorMessage(e), data: { tone: "danger" } });

  const forkOnline = useMutation({
    mutationFn: () => createDraft(name, { name: newDraftName.trim(), fromOnline: true }),
    onSuccess: (d) => {
      setNewDraftName("");
      setEditing({ id: d.id, name: d.name, text: JSON.stringify(d.schema, null, 2) });
      setDraftDiff(null);
      toast.add({ title: "Forked. Edit it below, then run it." });
      invalidateDrafts();
    },
    onError: fail,
  });
  const saveDraft = useMutation({
    mutationFn: (args: { id: string; name: string; text: string }) =>
      updateDraft(args.id, {
        name: args.name,
        schema: JSON.parse(args.text) as Record<string, unknown>,
      }),
    onSuccess: (d) => {
      setEditing({ id: d.id, name: d.name, text: JSON.stringify(d.schema, null, 2) });
      setDraftDiff(null);
      toast.add({ title: "Draft saved." });
      invalidateDrafts();
    },
    onError: fail,
  });
  const removeDraft = useMutation({
    mutationFn: (id: string) => deleteDraft(id),
    onSuccess: (_r, id) => {
      if (editing?.id === id) setEditing(null);
      if (draftDiff?.draftId === id) setDraftDiff(null);
      if (schemaChoice === id) setSchemaChoice("online");
      invalidateDrafts();
    },
    onError: fail,
  });
  const showDiff = useMutation({
    mutationFn: (id: string) => fetchDraftDiff(id),
    onSuccess: setDraftDiff,
    onError: fail,
  });

  const addTag = useMutation({
    mutationFn: () =>
      createTag(name, {
        name: draft.name.trim(),
        definition: draft.definition.trim(),
        answerType: draft.answerType,
        options:
          draft.answerType === "single_select"
            ? draft.options
                .split(",")
                .map((o) => o.trim())
                .filter(Boolean)
            : [],
        precondition: draft.precondition,
        scopeRef: draft.scopeRef.trim() || null,
      }),
    onSuccess: () => {
      setDraft({ ...draft, name: "", definition: "", options: "", scopeRef: "" });
      toast.add({ title: "Tag added. The next run asks it." });
      invalidateTags();
    },
    onError: fail,
  });

  const toggleActive = useMutation({
    mutationFn: (tag: TagDef) => updateTag(tag.id, { active: !tag.active }),
    onSuccess: invalidateTags,
    onError: fail,
  });

  // Editing a tag is editing its COPY — the name it is read by and the
  // definition the model is given. The answer shape, the precondition and the
  // options are NOT patchable (`TagDefUpdate` carries neither, and findings
  // already answered under the old shape would become unreadable), so a change
  // to those is a new tag plus a deactivation.
  const editTagSave = useMutation({
    mutationFn: (args: { id: string; name: string; definition: string }) =>
      updateTag(args.id, { name: args.name, definition: args.definition }),
    onSuccess: () => {
      setEditTag(null);
      toast.add({ title: "Tag saved. The next run asks the new wording." });
      invalidateTags();
    },
    onError: fail,
  });

  const runNow = useMutation({
    mutationFn: () =>
      startTagRun(name, {
        scopeKind,
        sinceDays,
        limit,
        projectIds:
          scopeKind === "explicit"
            ? projectIds
                .split(/[\s,]+/)
                .map((p) => p.trim())
                .filter(Boolean)
            : [],
        rerunOf: scopeKind === "rerun" ? rerunOf.trim() : null,
        batchId: scopeKind === "corpus" ? batchId : null,
        // "online" sends neither: the backend asks the ingested online schema,
        // or the vendored file when nothing is ingested yet, and the run row
        // says which. A draft wins over pasted text.
        schemaOverride:
          schemaChoice === "paste" && schemaText.trim()
            ? (JSON.parse(schemaText) as Record<string, unknown>)
            : null,
        draftId: schemaChoice !== "online" && schemaChoice !== "paste" ? schemaChoice : null,
        tagIds: onlyTags,
        filters: scopeKind === "recent" ? filters : null,
      }),
    onSuccess: () => {
      toast.add({ title: "Run queued. It appears below as the worker reaches it." });
      void qc.invalidateQueries({ queryKey: taggingKeys.runs(name) });
    },
    onError: fail,
  });

  const doImport = useMutation({
    mutationFn: async (args: { text: string; dryRun: boolean }) => {
      const parsed = JSON.parse(args.text) as { tags?: TagDef[] };
      return importTagset(name, {
        tags: parsed.tags ?? [],
        dryRun: args.dryRun,
        archiveMissing: false,
      });
    },
    onSuccess: (result, args) => {
      setDiff(result);
      if (!args.dryRun) {
        toast.add({ title: "Imported." });
        setImportText("");
        setDiff(null);
        invalidateTags();
      }
    },
    onError: fail,
  });

  if (taggers.isPending || tags.isPending) return <LoadingState label="Loading tagger…" />;
  if (tags.isError) return <ErrorState error={tags.error} onRetry={() => void tags.refetch()} />;
  if (!info) return <EmptyState title="Unknown tagger" message={name} />;

  const active = tags.data.filter((t) => t.active);

  return (
    <div className="bi-page">
      <Link to="/taggers" className="bi-back">
        ← All taggers
      </Link>
      <div className="bi-page__head">
        <h1 className="bi-page__title">{info.label}</h1>
        <p className="bi-page__sub">
          {info.description} It reads one <strong>{info.subjectKind}</strong> at a time.{" "}
          {info.ownsCall
            ? `It records what ${info.reportsFrom} decided as the product runs, and can also be run over past work.`
            : `It records what ${info.reportsFrom} decided, and cannot be run over past work: the evidence it judges is not stored.`}
          {info.deliversSchema
            ? " Its call asks with a strict schema, so some of what the model is told lives in the schema's field descriptions rather than in the prompt: read both together below."
            : " Its call sends no schema, so the prompt is the whole of what the model is told."}
          {canWrite ? "" : " You hold read access, so the write controls are hidden."}
        </p>
      </div>

      {/* ---- the vocabulary ---- */}
      <div className="bi-panel">
        <div className="bi-panel__head">
          <div>
            <h2 className="bi-panel__title">Tags</h2>
            <p className="bi-panel__note">
              A tag's definition IS the recognition rule: the tagger reads it and nothing else,
              which is why a new tag needs no deploy.
            </p>
          </div>
          <Badge size="sm" tone="neutral" variant="soft">
            {active.length} of {tags.data.length} active
          </Badge>
        </div>
        <div className="bi-table-wrap">
          <table className="bi-table">
            <thead>
              <tr>
                <th>Tag</th>
                <th>What the model is told</th>
                <th>Answer</th>
                <th>Only when</th>
                <th>{canWrite ? "" : "Active"}</th>
              </tr>
            </thead>
            <tbody>
              {tags.data.map((t) => (
                <tr
                  key={t.id}
                  className={
                    editTag?.id === t.id ? "is-selected" : t.active ? undefined : "is-muted"
                  }
                >
                  <td>
                    {editTag?.id === t.id ? (
                      <input
                        className="bi-input"
                        value={editTag.name}
                        onChange={(e) => setEditTag({ ...editTag, name: e.target.value })}
                      />
                    ) : (
                      <div>{t.name}</div>
                    )}
                    {t.isBuiltin ? (
                      <Badge size="sm" tone="neutral" variant="outline">
                        builtin
                      </Badge>
                    ) : null}
                  </td>
                  {/* The DESCRIPTION reads as a label; the PROMPT TEXT is what the
                      model actually gets. Showing only the first made this table
                      look authoritative while the prompt said something else, so
                      both are here and the prompt is the one in monospace. */}
                  <td className="bi-table__prose">
                    {editTag?.id === t.id ? (
                      <textarea
                        className="bi-textarea"
                        value={editTag.definition}
                        onChange={(e) => setEditTag({ ...editTag, definition: e.target.value })}
                      />
                    ) : (
                      <div>{t.definition}</div>
                    )}
                    {t.promptText ? (
                      <details style={{ marginTop: "var(--rc-space-1)" }}>
                        <summary className="bi-field__hint" style={{ cursor: "pointer" }}>
                          exact prompt text
                        </summary>
                        <pre className="bi-pre" style={{ marginTop: "var(--rc-space-1)" }}>
                          {t.promptText}
                        </pre>
                      </details>
                    ) : null}
                  </td>
                  <td>{answerShape(t)}</td>
                  <td>
                    {t.precondition === "none" ? "always" : t.precondition}
                    {t.scopeRef ? ` · ${t.scopeRef}` : ""}
                  </td>
                  <td>
                    {canWrite ? (
                      editTag?.id === t.id ? (
                        <div className="bi-page__actions">
                          <Button
                            size="sm"
                            onClick={() =>
                              editTagSave.mutate({
                                id: editTag.id,
                                name: editTag.name.trim(),
                                definition: editTag.definition.trim(),
                              })
                            }
                            disabled={
                              !editTag.name.trim() ||
                              !editTag.definition.trim() ||
                              editTagSave.isPending
                            }
                          >
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            tone="neutral"
                            onClick={() => setEditTag(null)}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <div className="bi-page__actions">
                          <Button
                            size="sm"
                            variant="ghost"
                            tone="neutral"
                            onClick={() =>
                              setEditTag({ id: t.id, name: t.name, definition: t.definition })
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            tone="neutral"
                            onClick={() => toggleActive.mutate(t)}
                          >
                            {t.active ? "Deactivate" : "Activate"}
                          </Button>
                        </div>
                      )
                    ) : (
                      <span>{t.active ? "yes" : "no"}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bi-panel__foot">
          A tag is deactivated, never deleted: findings reference its id. Edit changes its name
          and its definition from the next run on, and leaves findings already answered under the
          old wording as they are. The answer shape, the options and the precondition are not
          editable, because a finding answered under the old shape could not be read back: change
          those by adding a tag and deactivating this one.
        </div>
      </div>

      {/* ---- add, or say why not ---- */}
      {!info.ownsCall ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <h2 className="bi-panel__title">Read only, and why</h2>
          </div>
          <div className="bi-panel__body">
            <p className="bi-panel__note">
              This tagger records what <code>{info.reportsFrom}</code> already decided, and it
              cannot be run over past work. What it judges is a talent's profile and gig tiers as
              they were during a search: the gateway supplies those during the run and nothing
              stores them.
            </p>
            <p className="bi-panel__note" style={{ marginTop: "var(--rc-space-2)" }}>
              So a tag added here would never be answered by anything. No live call asks it, and no
              run can reconstruct what it would need to look at. Re-fetching would judge a different
              profile and call it the same one.
            </p>
          </div>
        </div>
      ) : null}
      {canWrite && info.ownsCall ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <h2 className="bi-panel__title">Add a tag</h2>
          </div>
          <div className="bi-panel__body">
            <div className="bi-form">
              <div className="bi-form__row">
                <label className="bi-field bi-field--grow">
                  <span className="bi-field__label">Name</span>
                  <input
                    className="bi-input"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Left without a brief"
                  />
                </label>
                <label className="bi-field">
                  <span className="bi-field__label">Answer</span>
                  <select
                    className="bi-select"
                    value={draft.answerType}
                    onChange={(e) => setDraft({ ...draft, answerType: e.target.value })}
                  >
                    {info.answerTypes.map((a) => (
                      <option key={a} value={a}>
                        {a}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="bi-field">
                  <span className="bi-field__label">Only when</span>
                  <select
                    className="bi-select"
                    value={draft.precondition}
                    onChange={(e) => setDraft({ ...draft, precondition: e.target.value })}
                  >
                    {info.preconditions.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <label className="bi-field">
                <span className="bi-field__label">What it asks</span>
                <textarea
                  className="bi-textarea"
                  value={draft.definition}
                  onChange={(e) => setDraft({ ...draft, definition: e.target.value })}
                  placeholder="The whole recognition rule, in full. The tagger reads this and nothing else."
                />
              </label>
              {draft.answerType === "single_select" ? (
                <label className="bi-field">
                  <span className="bi-field__label">Options</span>
                  <input
                    className="bi-input"
                    value={draft.options}
                    onChange={(e) => setDraft({ ...draft, options: e.target.value })}
                    placeholder="scoping, budget, waiting_on_talent"
                  />
                  <span className="bi-field__hint">
                    Comma separated. `not_applicable` is added for you, so the model can decline
                    inside a closed set.
                  </span>
                </label>
              ) : null}
              <div className="bi-page__actions">
                <Button
                  size="sm"
                  onClick={() => addTag.mutate()}
                  disabled={!draft.name.trim() || !draft.definition.trim() || addTag.isPending}
                >
                  Add tag
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {canWrite && info.ownsCall ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <div>
              <h2 className="bi-panel__title">Run</h2>
              <p className="bi-panel__note">
                A run spends real model money. It is queued to the worker and never blocks this
                page.
              </p>
            </div>
          </div>
          <div className="bi-panel__body">
            <div className="bi-form">
              <div className="bi-form__row">
                <label className="bi-field">
                  <span className="bi-field__label">Cover</span>
                  <select
                    className="bi-select"
                    value={scopeKind}
                    onChange={(e) =>
                      setScopeKind(e.target.value as "recent" | "explicit" | "rerun" | "corpus")
                    }
                  >
                    <option value="recent">Recent subjects</option>
                    <option value="explicit">These projects</option>
                    <option value="rerun">Same subjects as an earlier run</option>
                    <option value="corpus">An uploaded corpus</option>
                  </select>
                </label>
                {scopeKind === "recent" ? (
                  <>
                    <label className="bi-field">
                      <span className="bi-field__label">Since (days)</span>
                      <input
                        className="bi-input"
                        type="number"
                        min={1}
                        max={30}
                        value={sinceDays}
                        onChange={(e) => setSinceDays(Number(e.target.value))}
                      />
                    </label>
                  </>
                ) : null}
                {/* "At most" is sent on EVERY scope, so it has to be visible on
                    every scope that reads it. Hidden, a corpus run silently took
                    the default 25 of an uploaded 500. */}
                {scopeKind === "recent" || scopeKind === "corpus" ? (
                  <label className="bi-field">
                    <span className="bi-field__label">At most</span>
                    <input
                      className="bi-input"
                      type="number"
                      min={1}
                      value={limit}
                      onChange={(e) => setLimit(Number(e.target.value))}
                    />
                    {scopeKind === "corpus" ? (
                      <span className="bi-field__hint">
                        {selectedCorpus
                          ? `This corpus holds ${selectedCorpus.nProjects} projects. Set this to that number to cover all of them.`
                          : "A run covers as many subjects as its scope holds."}
                      </span>
                    ) : null}
                  </label>
                ) : null}
                {scopeKind === "corpus" ? (
                  <label className="bi-field bi-field--grow">
                    <span className="bi-field__label">Corpus</span>
                    <select
                      className="bi-select"
                      value={batchId}
                      onChange={(e) => setBatchId(e.target.value)}
                    >
                      <option value="">Pick an uploaded batch</option>
                      {(corpora.data ?? []).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.label} · {b.nProjects} projects · {b.sourceEnv || "unknown"}
                        </option>
                      ))}
                    </select>
                    <span className="bi-field__hint">
                      Real conversations from another environment. Nothing here is a local project,
                      so these findings never touch a client's record.
                    </span>
                  </label>
                ) : null}
                {scopeKind === "rerun" ? (
                  <label className="bi-field bi-field--grow">
                    <span className="bi-field__label">Run to repeat</span>
                    <select
                      className="bi-select"
                      value={rerunOf}
                      onChange={(e) => setRerunOf(e.target.value)}
                    >
                      <option value="">Pick a run</option>
                      {(runs.data ?? []).map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.createdAt.slice(0, 16).replace("T", " ")} · {r.nSubjects} subjects
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
              </div>
              {scopeKind === "recent" ? (
                <div className="bi-field">
                  <span className="bi-field__label">Only projects that</span>
                  <span className="bi-field__hint">
                    Objective facts the product already stores, applied before the model sees
                    anything; every default is &quot;don&apos;t care&quot;. Clients who finished the
                    brief and went quiet: Brief = finished, Not a talent, Idle 24.
                  </span>
                  <div className="bi-form__row">
                    <label className="bi-field">
                      <span className="bi-field__label">Brief</span>
                      <select
                        className="bi-select"
                        value={filters.briefStatus}
                        onChange={(e) =>
                          setFilter("briefStatus", e.target.value as SubjectFilters["briefStatus"])
                        }
                      >
                        <option value="any">Any</option>
                        <option value="finished">Finished (approved or searching)</option>
                        <option value="draft">Still a draft</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Client intent (PULSE)</span>
                      <select
                        className="bi-select"
                        value={filters.clientIntent}
                        onChange={(e) =>
                          setFilter(
                            "clientIntent",
                            e.target.value as SubjectFilters["clientIntent"],
                          )
                        }
                      >
                        <option value="any">Any</option>
                        <option value="high">High</option>
                        <option value="low">Low</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Engagement</span>
                      <select
                        className="bi-select"
                        value={filters.engagementType}
                        onChange={(e) =>
                          setFilter(
                            "engagementType",
                            e.target.value as SubjectFilters["engagementType"],
                          )
                        }
                      >
                        <option value="any">Any</option>
                        <option value="project">Project</option>
                        <option value="hiring">Hiring</option>
                      </select>
                    </label>
                  </div>
                  <div className="bi-form__row">
                    <label className="bi-field">
                      <span className="bi-field__label">Ran a search</span>
                      <select
                        className="bi-select"
                        value={filters.hasSearchRun}
                        onChange={(e) => setFilter("hasSearchRun", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Search as you go</span>
                      <select
                        className="bi-select"
                        value={filters.hasSearchAsYouGo}
                        onChange={(e) => setFilter("hasSearchAsYouGo", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Concierge run</span>
                      <select
                        className="bi-select"
                        value={filters.hasConciergeRun}
                        onChange={(e) => setFilter("hasConciergeRun", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Got an offer</span>
                      <select
                        className="bi-select"
                        value={filters.hasOffer}
                        onChange={(e) => setFilter("hasOffer", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Picked a talent (hand-off)</span>
                      <select
                        className="bi-select"
                        value={filters.hasHandoff}
                        onChange={(e) => setFilter("hasHandoff", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                  </div>
                  <div className="bi-form__row">
                    <label className="bi-field">
                      <span className="bi-field__label">Company size</span>
                      <select
                        className="bi-select"
                        multiple
                        size={6}
                        value={filters.companySizes}
                        onChange={(e) =>
                          setFilter(
                            "companySizes",
                            Array.from(e.target.selectedOptions, (o) => o.value) as
                              SubjectFilters["companySizes"],
                          )
                        }
                      >
                        <option value="only_me">Just them (only_me)</option>
                        <option value="between_2_and_10">2-10</option>
                        <option value="between_11_and_50">11-50 (SMB)</option>
                        <option value="between_51_and_500">51-500</option>
                        <option value="more_than_500">500+</option>
                        <option value="unknown">Unknown (no size on the account)</option>
                      </select>
                      <span className="bi-field__hint">
                        The buyer&apos;s OWN account record, not a judgement: this is how you
                        carve a cohort the model is then asked about. Pick none for every
                        size. &ldquo;Unknown&rdquo; is ~86% of projects and is the cohort
                        where the model has to work unaided.
                      </span>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Has an account profile</span>
                      <select
                        className="bi-select"
                        value={filters.hasBuyerIdentity}
                        onChange={(e) => setFilter("hasBuyerIdentity", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No (guests and pre-seed sign-ins)</option>
                      </select>
                    </label>
                  </div>
                  <div className="bi-form__row">
                    <label className="bi-field">
                      <span className="bi-field__label">Account names a company</span>
                      <select
                        className="bi-select"
                        value={filters.hasCompany}
                        onChange={(e) => setFilter("hasCompany", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Account names a website</span>
                      <select
                        className="bi-select"
                        value={filters.hasWebsite}
                        onChange={(e) => setFilter("hasWebsite", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Account names an industry</span>
                      <select
                        className="bi-select"
                        value={filters.hasIndustry}
                        onChange={(e) => setFilter("hasIndustry", e.target.value as Tri)}
                      >
                        <option value="any">Any</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </label>
                  </div>
                  <div className="bi-form__row">
                    <label className="bi-field">
                      <span className="bi-field__label">Idle for (hours)</span>
                      <input
                        className="bi-input"
                        type="number"
                        min={0}
                        placeholder="any"
                        value={filters.idleHours ?? ""}
                        onChange={(e) => setFilter("idleHours", numberOrNull(e.target.value))}
                      />
                      <span className="bi-field__hint">
                        No message from anyone for this long: the conversation is over.
                      </span>
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Client messages, at least</span>
                      <input
                        className="bi-input"
                        type="number"
                        min={0}
                        placeholder="any"
                        value={filters.minMessages ?? ""}
                        onChange={(e) => setFilter("minMessages", numberOrNull(e.target.value))}
                      />
                    </label>
                    <label className="bi-field">
                      <span className="bi-field__label">Client messages, at most</span>
                      <input
                        className="bi-input"
                        type="number"
                        min={0}
                        placeholder="any"
                        value={filters.maxMessages ?? ""}
                        onChange={(e) => setFilter("maxMessages", numberOrNull(e.target.value))}
                      />
                    </label>
                  </div>
                  <fieldset className="bi-checks">
                    <label className="bi-checks__item">
                      <input
                        type="checkbox"
                        checked={filters.excludeSellers}
                        onChange={(e) => setFilter("excludeSellers", e.target.checked)}
                      />
                      Not a talent (no seller_intent flag)
                    </label>
                    <label className="bi-checks__item">
                      <input
                        type="checkbox"
                        checked={filters.excludeGuests}
                        onChange={(e) => setFilter("excludeGuests", e.target.checked)}
                      />
                      Signed-in accounts only
                    </label>
                  </fieldset>
                </div>
              ) : null}
              {scopeKind === "explicit" ? (
                <label className="bi-field">
                  <span className="bi-field__label">Project ids</span>
                  <textarea
                    className="bi-textarea"
                    value={projectIds}
                    onChange={(e) => setProjectIds(e.target.value)}
                    placeholder="One per line, or comma separated."
                  />
                </label>
              ) : null}
              <div className="bi-field">
                <span className="bi-field__label">Ask only these tags</span>
                <span className="bi-field__hint">
                  Empty means every active tag. Picking the ones you just changed re-tags the same
                  evidence for a fraction of the cost.
                </span>
                <fieldset className="bi-checks">
                  {active.map((t) => (
                    <label key={t.id} className="bi-checks__item">
                      <input
                        type="checkbox"
                        checked={onlyTags.includes(t.id)}
                        onChange={(e) =>
                          setOnlyTags(
                            e.target.checked
                              ? [...onlyTags, t.id]
                              : onlyTags.filter((x) => x !== t.id),
                          )
                        }
                      />
                      {t.name}
                    </label>
                  ))}
                </fieldset>
              </div>
              {info.schemaName ? (
                <label className="bi-field">
                  <span className="bi-field__label">Schema</span>
                  <span className="bi-field__hint">
                    Which prompt this run asks. Online is what the product sends today; a draft
                    or pasted JSON applies to this run only and is never stored where a live call
                    could read it, so nothing tried here reaches production without a commit.
                  </span>
                  <select
                    className="bi-select"
                    value={schemaChoice}
                    onChange={(e) => setSchemaChoice(e.target.value)}
                  >
                    <option value="online">
                      {online.data
                        ? `Online (${shortSha(online.data.sha256)})`
                        : "Online (nothing ingested yet: the file in this image)"}
                    </option>
                    {(drafts.data ?? []).map((d) => (
                      <option key={d.id} value={d.id}>
                        Draft: {d.name || shortSha(d.id)} ({d.state})
                      </option>
                    ))}
                    <option value="paste">Paste JSON</option>
                  </select>
                  {schemaChoice === "paste" ? (
                    <textarea
                      className="bi-textarea"
                      value={schemaText}
                      onChange={(e) => setSchemaText(e.target.value)}
                      placeholder="The whole schema JSON"
                    />
                  ) : null}
                </label>
              ) : null}
              <div className="bi-page__actions">
                <Button
                  size="sm"
                  onClick={() => runNow.mutate()}
                  disabled={runNow.isPending || (scopeKind === "corpus" && !batchId)}
                >
                  Start run
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* ---- online: what the product asks right now ---- */}
      {info.schemaName ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <div>
              <h2 className="bi-panel__title">Online</h2>
              <p className="bi-panel__note">
                The schema file committed in recruiter and deployed with it. Code is the source of
                truth; nothing on this page can change it. This is what recruiter&apos;s worker
                published at boot and this service last ingested.
              </p>
            </div>
            {online.data ? (
              <div className="bi-page__actions">
                <Button
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  onClick={() => setShowOnline((v) => !v)}
                >
                  {showOnline ? "Hide" : "Show"} JSON
                </Button>
                {canWrite ? (
                  <Button
                    size="sm"
                    onClick={() => forkOnline.mutate()}
                    disabled={forkOnline.isPending}
                  >
                    Fork online
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
          {online.isPending ? (
            <div className="bi-empty">Reading the online schema…</div>
          ) : online.isError ? (
            <div className="bi-empty">{errorMessage(online.error)}</div>
          ) : online.data == null ? (
            <div className="bi-empty">
              Nothing online has been ingested yet. Recruiter&apos;s worker publishes the schema
              it shipped, and this service reads it on the five-minute tick, which needs a corpus
              source configured. Until then a run asks the copy shipped inside this image and its
              row says so (&quot;vendored&quot;).
            </div>
          ) : (
            <div className="bi-panel__body">
              <div className="bi-form__row">
                <div className="bi-field">
                  <span className="bi-field__label">Schema sha</span>
                  <code title={online.data.sha256}>{shortSha(online.data.sha256)}</code>
                </div>
                <div className="bi-field">
                  <span className="bi-field__label">Release</span>
                  <code>{online.data.releaseSha ? shortSha(online.data.releaseSha) : "local"}</code>
                  {online.data.serviceVersion ? (
                    <span className="bi-field__hint">{online.data.serviceVersion}</span>
                  ) : null}
                </div>
                <div className="bi-field">
                  <span className="bi-field__label">Published</span>
                  <span>{when(online.data.publishedAt)}</span>
                  {online.data.appEnv ? (
                    <span className="bi-field__hint">{online.data.appEnv}</span>
                  ) : null}
                </div>
                <div className="bi-field">
                  <span className="bi-field__label">Last seen here</span>
                  <span>{when(online.data.seenAt)}</span>
                </div>
                <div className="bi-field">
                  <span className="bi-field__label">Copy in this image</span>
                  {online.data.matchesVendored ? (
                    <Badge size="sm" tone="success" variant="soft">
                      matches
                    </Badge>
                  ) : (
                    <Badge size="sm" tone="warning" variant="soft">
                      differs ({shortSha(online.data.vendoredSha) || "none"})
                    </Badge>
                  )}
                </div>
              </div>
              {canWrite ? (
                <label className="bi-field" style={{ marginTop: "var(--rc-space-2)" }}>
                  <span className="bi-field__label">Name the fork (optional)</span>
                  <input
                    className="bi-input"
                    value={newDraftName}
                    onChange={(e) => setNewDraftName(e.target.value)}
                    placeholder="sharper wants-a-human wording"
                  />
                </label>
              ) : null}
              {showOnline ? (
                <pre className="bi-pre" style={{ marginTop: "var(--rc-space-2)" }}>
                  {JSON.stringify(online.data.schema, null, 2)}
                </pre>
              ) : null}
            </div>
          )}
        </div>
      ) : null}

      {/* ---- drafts: fork, edit, run, diff, download ---- */}
      {info.schemaName ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <div>
              <h2 className="bi-panel__title">Drafts</h2>
              <p className="bi-panel__note">
                A draft is forked from online, edited here, and run offline. The way it ships is
                that you download the JSON and an engineer commits it to{" "}
                <code>recruiter/tagging/schema/</code>; after that deploy this page notices online
                equals the draft and marks it promoted.
              </p>
            </div>
          </div>
          {(drafts.data ?? []).length === 0 ? (
            <div className="bi-empty">No drafts yet. Fork the online schema to start one.</div>
          ) : (
            <div className="bi-table-wrap">
              <table className="bi-table">
                <thead>
                  <tr>
                    <th>Draft</th>
                    <th>State</th>
                    <th>Sha</th>
                    <th>Forked from</th>
                    <th>Updated</th>
                    <th>By</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(drafts.data ?? []).map((d: TagDraft) => (
                    <tr key={d.id} className={editing?.id === d.id ? "is-selected" : undefined}>
                      <td>{d.name || shortSha(d.id)}</td>
                      <td>
                        <DraftState state={d.state} />
                        {d.promotedAt ? (
                          <div className="bi-field__hint">promoted {when(d.promotedAt)}</div>
                        ) : null}
                      </td>
                      <td>
                        <code title={d.sha256}>{shortSha(d.sha256)}</code>
                      </td>
                      <td>
                        <code>{shortSha(d.forkedFromSha) || "pasted"}</code>
                      </td>
                      <td>{when(d.updatedAt)}</td>
                      <td>{d.createdBy}</td>
                      <td>
                        {canWrite ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            tone="neutral"
                            onClick={() => {
                              setEditing({
                                id: d.id,
                                name: d.name,
                                text: JSON.stringify(d.schema, null, 2),
                              });
                              setDraftDiff(null);
                            }}
                          >
                            Edit
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="ghost"
                          tone="neutral"
                          disabled={showDiff.isPending}
                          onClick={() => showDiff.mutate(d.id)}
                        >
                          Diff vs online
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          tone="neutral"
                          onClick={() => {
                            downloadDraft(d.id)
                              .then((text) => downloadText(`${info.schemaName}.json`, text))
                              .catch(fail);
                          }}
                        >
                          Download JSON
                        </Button>
                        {canWrite ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            tone="danger"
                            disabled={removeDraft.isPending}
                            onClick={() => {
                              if (window.confirm("Delete this draft? Its runs stay.")) {
                                removeDraft.mutate(d.id);
                              }
                            }}
                          >
                            Delete
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {editing && canWrite ? (
            <div className="bi-panel__body">
              <div className="bi-form">
                <label className="bi-field">
                  <span className="bi-field__label">Name</span>
                  <input
                    className="bi-input"
                    value={editing.name}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  />
                </label>
                <label className="bi-field">
                  <span className="bi-field__label">Schema JSON</span>
                  <textarea
                    className="bi-textarea"
                    style={{ minHeight: "20rem", fontFamily: "monospace" }}
                    value={editing.text}
                    onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                  />
                </label>
                <div className="bi-page__actions">
                  <Button
                    size="sm"
                    disabled={saveDraft.isPending}
                    onClick={() => {
                      try {
                        JSON.parse(editing.text);
                      } catch (e) {
                        fail(e);
                        return;
                      }
                      saveDraft.mutate(editing);
                    }}
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" tone="neutral" onClick={() => setEditing(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          {draftDiff ? (
            <div className="bi-panel__body">
              <div className="bi-panel__head">
                <div>
                  <h3 className="bi-prompt-block__title">
                    Diff: {(drafts.data ?? []).find((d) => d.id === draftDiff.draftId)?.name ||
                      shortSha(draftDiff.draftId)}{" "}
                    <DraftState state={draftDiff.state} />
                  </h3>
                  <p className="bi-panel__note">{diffSummary(draftDiff)}</p>
                </div>
                <Button size="sm" variant="ghost" tone="neutral" onClick={() => setDraftDiff(null)}>
                  Close
                </Button>
              </div>
              {draftDiff.blocksAdded.length ? (
                <p className="bi-field__hint">Blocks added: {draftDiff.blocksAdded.join(", ")}</p>
              ) : null}
              {draftDiff.blocksRemoved.length ? (
                <p className="bi-field__hint">
                  Blocks removed: {draftDiff.blocksRemoved.join(", ")}
                </p>
              ) : null}
              {draftDiff.blocks
                .filter((b) => b.changed)
                .map((b) => (
                  <section key={b.key} className="bi-prompt-block">
                    <h3 className="bi-prompt-block__title">
                      {b.key}
                      <span className="bi-field__hint"> · {blockDiffLines(b).length} changes</span>
                    </h3>
                    {b.preambleDiff.length ? <pre className="bi-pre">{b.preambleDiff.join("\n")}</pre> : null}
                    {b.tagsAdded.length || b.tagsRemoved.length || b.tagsChanged.length ? (
                      <div className="bi-table-wrap">
                        <table className="bi-table">
                          <thead>
                            <tr>
                              <th>Tag</th>
                              <th>Field</th>
                              <th>Online</th>
                              <th>Draft</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.tagsAdded.map((id) => (
                              <tr key={`add-${id}`}>
                                <td>{id}</td>
                                <td>added</td>
                                <td></td>
                                <td>new</td>
                              </tr>
                            ))}
                            {b.tagsRemoved.map((id) => (
                              <tr key={`rm-${id}`}>
                                <td>{id}</td>
                                <td>removed</td>
                                <td>was asked</td>
                                <td></td>
                              </tr>
                            ))}
                            {b.tagsChanged.map((c) => (
                              <tr key={`${c.id}-${c.field}`}>
                                <td>{c.id}</td>
                                <td>{c.field}</td>
                                <td className="bi-table__prose">{changeValue(c.before)}</td>
                                <td className="bi-table__prose">{changeValue(c.after)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    {b.propsAdded.length || b.propsRemoved.length || b.propsChanged.length ? (
                      <div className="bi-table-wrap">
                        <table className="bi-table">
                          <thead>
                            <tr>
                              <th>Response schema property</th>
                              <th>Online</th>
                              <th>Draft</th>
                            </tr>
                          </thead>
                          <tbody>
                            {b.propsAdded.map((p) => (
                              <tr key={`padd-${p}`}>
                                <td>{p}</td>
                                <td></td>
                                <td>added</td>
                              </tr>
                            ))}
                            {b.propsRemoved.map((p) => (
                              <tr key={`prm-${p}`}>
                                <td>{p}</td>
                                <td>removed</td>
                                <td></td>
                              </tr>
                            ))}
                            {b.propsChanged.map((c) => (
                              <tr key={c.path}>
                                <td>{c.path}</td>
                                <td className="bi-table__prose">{changeValue(c.before)}</td>
                                <td className="bi-table__prose">{changeValue(c.after)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                  </section>
                ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ---- the schema: what the model is actually asked ---- */}
      {info.schemaName ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <div>
              <h2 className="bi-panel__title">The prompt, assembled</h2>
              <p className="bi-panel__note">
                What the copy of the schema shipped inside this image assembles to, block by
                block. The Online panel above says whether that copy is what the product sends
                today; a run asks the online one whenever it has been ingested.
              </p>
            </div>
            <div className="bi-page__actions">
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                onClick={() => setShowPrompt((v) => !v)}
              >
                {showPrompt ? "Hide" : "Show"} assembled prompt
              </Button>
              <Button
                size="sm"
                variant="ghost"
                tone="neutral"
                disabled={!schema.data}
                onClick={() => {
                  if (schema.data) downloadJson(`${info.schemaName}.json`, schema.data.schema);
                }}
              >
                Download schema
              </Button>
            </div>
          </div>
          {showPrompt && schema.data ? (
            <div className="bi-panel__body">
              {schema.data.blocks.map((block) => (
                <section key={block.key} className="bi-prompt-block">
                  {schema.data.blocks.length > 1 ? (
                    <h3 className="bi-prompt-block__title">
                      {block.key}
                      <span className="bi-field__hint"> · {block.tagIds.join(", ")}</span>
                    </h3>
                  ) : null}
                  <pre className="bi-pre">{block.prompt}</pre>
                  {/* The other half of the question. These calls ask with a strict
                      schema, and a rule in a field description reaches the model
                      exactly as a sentence in the prompt does, so showing the
                      prompt alone would understate what was asked. */}
                  {block.responseSchema ? (
                    <details className="bi-prompt-block__schema">
                      <summary className="bi-field__hint">
                        and the response schema it is asked with
                      </summary>
                      <pre className="bi-pre">{JSON.stringify(block.responseSchema, null, 2)}</pre>
                    </details>
                  ) : (
                    <p className="bi-field__hint">
                      This call sends no schema, so the prompt above is the whole of what the model
                      is told.
                    </p>
                  )}
                </section>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* ---- export / import ---- */}
      <div className="bi-panel">
        <div className="bi-panel__head">
          <div>
            <h2 className="bi-panel__title">Move this vocabulary</h2>
            <p className="bi-panel__note">
              Export is exactly what import takes back, so a tag set round-trips between
              environments without being retyped.
            </p>
          </div>
          <Button
            size="sm"
            variant="ghost"
            tone="neutral"
            onClick={() => {
              void exportTagset(name).then((set) => downloadJson(`${name}-tags.json`, set));
            }}
          >
            Download JSON
          </Button>
        </div>
        {canWrite ? (
          <div className="bi-panel__body">
            <div className="bi-form">
              <label className="bi-field">
                <span className="bi-field__label">Paste a tag set</span>
                <textarea
                  className="bi-textarea"
                  value={importText}
                  onChange={(e) => {
                    setImportText(e.target.value);
                    setDiff(null);
                  }}
                  placeholder='{"tagger": "...", "tags": [...]}'
                />
              </label>
              <div className="bi-page__actions">
                <Button
                  size="sm"
                  variant="ghost"
                  tone="neutral"
                  onClick={() => doImport.mutate({ text: importText, dryRun: true })}
                  disabled={!importText.trim() || doImport.isPending}
                >
                  Preview changes
                </Button>
                {diff ? (
                  <Button
                    size="sm"
                    onClick={() => doImport.mutate({ text: importText, dryRun: false })}
                    disabled={diff.errors.length > 0 || doImport.isPending}
                  >
                    Apply
                  </Button>
                ) : null}
              </div>
              {diff ? (
                <p className="bi-field__hint">
                  {diff.added.length} added, {diff.updated.length} updated, {diff.unchanged.length}{" "}
                  unchanged
                  {diff.errors.length ? `, ${diff.errors.length} rejected` : ""}.
                  {diff.errors.length ? ` ${diff.errors.join(" ")}` : ""}
                </p>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      <div className="bi-panel">
        <div className="bi-panel__head">
          <h2 className="bi-panel__title">Runs</h2>
          <span className="bi-panel__note">Newest first.</span>
        </div>
        {(runs.data ?? []).length === 0 ? (
          <div className="bi-empty">No runs yet.</div>
        ) : (
          <div className="bi-table-wrap">
            <table className="bi-table">
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Cover</th>
                  <th>Schema</th>
                  <th className="bi-table__num">Subjects</th>
                  <th className="bi-table__num">Findings</th>
                  <th className="bi-table__num">Cost</th>
                  <th>By</th>
                  <th>Export</th>
                </tr>
              </thead>
              <tbody>
                {(runs.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>{r.createdAt.slice(0, 16).replace("T", " ")}</td>
                    <td>
                      <RunStatus status={r.status} />
                      {r.error ? <div className="bi-field__hint">{r.error}</div> : null}
                    </td>
                    <td>{r.scopeKind}</td>
                    <td>
                      <Badge
                        size="sm"
                        tone={
                          (r.schemaSource ?? "").startsWith("draft:") || r.schemaSource === "override"
                            ? "warning"
                            : !r.schemaSource || r.schemaSource === "vendored"
                              ? "neutral"
                              : "info"
                        }
                        variant="outline"
                        title={r.schemaSha}
                      >
                        {schemaSourceLabel(r.schemaSource, r.schemaSha, drafts.data ?? [])}
                      </Badge>
                    </td>
                    <td className="bi-table__num">{r.nSubjects}</td>
                    <td className="bi-table__num">{r.nFindings}</td>
                    <td className="bi-table__num">${r.costUsd.toFixed(2)}</td>
                    <td>{r.requestedBy}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="ghost"
                        tone="neutral"
                        // A run still going would hand back a partial file that
                        // looks complete once it is on disk. A run with NO
                        // findings still gets a report: "asked 300 subjects,
                        // nothing fired" is a result somebody has to be able to
                        // send on, and the report says so in those words.
                        disabled={r.status !== "done" || downloading === r.id}
                        title="A readable HTML report of this run: its numbers, what each tag found, and every finding"
                        onClick={() => {
                          setDownloading(r.id);
                          Promise.all([fetchRunFindings(r.id), fetchRunCounts(r.id)])
                            .then(([findings, counts]) => {
                              downloadText(
                                `${runFileStem(r, name)}.html`,
                                runReportHtml({
                                  run: r,
                                  tagger: info,
                                  tags: tags.data ?? [],
                                  counts,
                                  findings,
                                }),
                                "text/html;charset=utf-8",
                              );
                            })
                            .catch(fail)
                            .finally(() => setDownloading(""));
                        }}
                      >
                        {downloading === r.id ? "Preparing…" : "Report"}
                      </Button>
                      {/* The DEBUG file: everything the console can see about
                          this run, as one JSON object. The report beside it is
                          not enough to judge an answer: a yes/no tag stores
                          nothing for a false, and the transcript the model read
                          is on no page. The inputs half is empty for a run from
                          before the capture shipped, or whose inputs expired;
                          the file notes that. */}
                      <Button
                        size="sm"
                        variant="ghost"
                        tone="neutral"
                        disabled={r.status !== "done" || debugging === r.id}
                        title="Everything: the run, the tag definitions, the counts, the findings, and what each subject was sent and replied"
                        onClick={() => {
                          setDebugging(r.id);
                          Promise.all([
                            fetchRunFindings(r.id),
                            fetchRunInputs(r.id),
                            fetchRunCounts(r.id),
                          ])
                            .then(([findings, inputs, counts]) => {
                              downloadJson(
                                `${runFileStem(r, name)}-debug.json`,
                                runDebugBundle({
                                  run: r,
                                  tagger: info,
                                  tags: tags.data ?? [],
                                  counts,
                                  findings,
                                  inputs,
                                }),
                              );
                              if (inputs.length === 0) {
                                toast.add({
                                  title:
                                    "No inputs stored for this run: the file has everything else. A run captures what it sent from migration 0192 onward, and those rows expire after 14 days.",
                                });
                              }
                            })
                            .catch(fail)
                            .finally(() => setDebugging(""));
                        }}
                      >
                        {debugging === r.id ? "Preparing…" : "Debug JSON"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(runs.data ?? []).length >= 2 ? (
        <div className="bi-panel">
          <div className="bi-panel__head">
            <div>
              <h2 className="bi-panel__title">Compare runs</h2>
              <p className="bi-panel__note">
                Two runs joined on (subject, tag): a draft beside an online run over the same
                subjects is how a wording change is measured. Counts are over the whole join; the
                table is one page of it.
              </p>
            </div>
          </div>
          <div className="bi-panel__body">
            <div className="bi-form__row">
              <label className="bi-field bi-field--grow">
                <span className="bi-field__label">Run A</span>
                <select className="bi-select" value={cmpA} onChange={(e) => setCmpA(e.target.value)}>
                  <option value="">Pick a run</option>
                  {(runs.data ?? [])
                    .filter((r) => r.status === "done")
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {when(r.createdAt)} · {schemaSourceLabel(r.schemaSource, r.schemaSha, drafts.data ?? [])} · {r.nSubjects} subjects
                      </option>
                    ))}
                </select>
              </label>
              <label className="bi-field bi-field--grow">
                <span className="bi-field__label">Run B</span>
                <select className="bi-select" value={cmpB} onChange={(e) => setCmpB(e.target.value)}>
                  <option value="">Pick a run</option>
                  {(runs.data ?? [])
                    .filter((r) => r.status === "done")
                    .map((r) => (
                      <option key={r.id} value={r.id}>
                        {when(r.createdAt)} · {schemaSourceLabel(r.schemaSource, r.schemaSha, drafts.data ?? [])} · {r.nSubjects} subjects
                      </option>
                    ))}
                </select>
              </label>
              <label className="bi-field">
                <span className="bi-field__label">Show</span>
                <select
                  className="bi-select"
                  value={cmpStatus}
                  onChange={(e) => setCmpStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="changed">Changed</option>
                  <option value="same">Same</option>
                  <option value="only_a">Only in A</option>
                  <option value="only_b">Only in B</option>
                </select>
              </label>
            </div>
            {compare.isError ? <p className="bi-field__hint">{errorMessage(compare.error)}</p> : null}
            {compare.data ? (
              <>
                <p className="bi-panel__note" style={{ marginTop: "var(--rc-space-2)" }}>
                  {compare.data.same} same, {compare.data.changed} changed, {compare.data.onlyA}{" "}
                  only in A, {compare.data.onlyB} only in B.
                  {compare.data.total > compare.data.rows.length
                    ? ` Showing the first ${compare.data.rows.length} of ${compare.data.total}.`
                    : ""}
                </p>
                {compare.data.perTag.length ? (
                  <div className="bi-table-wrap">
                    <table className="bi-table">
                      <thead>
                        <tr>
                          <th>Tag</th>
                          <th className="bi-table__num">Same</th>
                          <th className="bi-table__num">Changed</th>
                          <th className="bi-table__num">Only A</th>
                          <th className="bi-table__num">Only B</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compare.data.perTag.map((t) => (
                          <tr key={t.tagId}>
                            <td>{t.tagName}</td>
                            <td className="bi-table__num">{t.same}</td>
                            <td className="bi-table__num">{t.changed}</td>
                            <td className="bi-table__num">{t.onlyA}</td>
                            <td className="bi-table__num">{t.onlyB}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : null}
                {compare.data.rows.length === 0 ? (
                  <div className="bi-empty">Nothing to show for that filter.</div>
                ) : (
                  <div className="bi-table-wrap">
                    <table className="bi-table">
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>Tag</th>
                          <th>Status</th>
                          <th>A said</th>
                          <th>B said</th>
                        </tr>
                      </thead>
                      <tbody>
                        {compare.data.rows.map((row) => (
                          <tr key={`${row.subjectRef}-${row.tagId}`}>
                            <td>
                              <div>{row.projectName || row.subjectRef}</div>
                              {row.projectName ? (
                                <div className="bi-field__hint">{row.subjectRef}</div>
                              ) : null}
                            </td>
                            <td>{row.tagName}</td>
                            <td>
                              <Badge
                                size="sm"
                                tone={
                                  row.status === "same"
                                    ? "neutral"
                                    : row.status === "changed"
                                      ? "warning"
                                      : "info"
                                }
                                variant="soft"
                              >
                                {row.status.replace("_", " ")}
                              </Badge>
                            </td>
                            <td className="bi-table__prose">
                              <div>{row.answerA ?? ""}</div>
                              {row.evidenceA ? (
                                <div className="bi-field__hint">{row.evidenceA}</div>
                              ) : null}
                            </td>
                            <td className="bi-table__prose">
                              <div>{row.answerB ?? ""}</div>
                              {row.evidenceB ? (
                                <div className="bi-field__hint">{row.evidenceB}</div>
                              ) : null}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
